// frontend/src/pages/Dashboard.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Fade,
  useTheme,
  Divider,
  LinearProgress,
  Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { fetchCreditBalance } from "../store/slices/creditSlice";
import CreditBalanceCard from "../components/CreditBalanceCard";
import RecentTransactionsTable from "../components/RecentTransactionsTable";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { esimAPI, transactionAPI } from "../services/api";
import DashboardStats from "../components/DashboardStats";
import ESIMStatusChart from "../components/charts/ESIMStatusChart";
import ESIMActivityTimeline from "../components/ESIMActivityTimeline";
import SpendingTrendsChart from "../components/charts/SpendingTrendsChart";
import useMediaQuery from "@mui/material/useMediaQuery";

const Dashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { balance, loading } = useSelector((state) => state.credits);
  const { user } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [stats, setStats] = useState({
    totalOrders: 0,
    activeESIMs: 0,
    pendingESIMs: 0,
    recentTransactions: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [transactionStats, setTransactionStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [dataUsageTrend, setDataUsageTrend] = useState({
    percentage: 0,
    increasing: false,
  });

  // Calculate real usage trend based on transaction data
  const calculateUsageTrend = (transactions) => {
    if (!transactions || transactions.length < 2) {
      return { percentage: 0, increasing: false };
    }

    // Get esim purchases only
    const esimPurchases = transactions.filter(
      (t) => t.type === "esim_purchase"
    );

    if (esimPurchases.length < 2) {
      return { percentage: 0, increasing: false };
    }

    // Sort by date
    const sortedPurchases = [...esimPurchases].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    // Get most recent and previous purchase amounts
    const latestAmount = Math.abs(
      Number(sortedPurchases[sortedPurchases.length - 1].amount)
    );
    const previousAmount = Math.abs(
      Number(sortedPurchases[sortedPurchases.length - 2].amount)
    );

    // Calculate percentage change
    if (previousAmount === 0) {
      return { percentage: 0, increasing: false };
    }

    const change = ((latestAmount - previousAmount) / previousAmount) * 100;

    return {
      percentage: Math.abs(change.toFixed(1)),
      increasing: change > 0,
    };
  };

  const fetchDashboardData = useCallback(async () => {
    setLoadingStats(true);
    try {
      // Fetch orders
      const ordersResponse = await esimAPI.getRecentOrders();
      setRecentOrders(ordersResponse.data.orders || []);

      // Fetch transactions
      const transResponse = await transactionAPI.getTransactions({ limit: 10 });
      const transactions = transResponse.data.transactions || [];
      setTransactionStats(transactions);

      // Calculate real usage trend from transaction data
      const usageTrend = calculateUsageTrend(transactions);
      setDataUsageTrend(usageTrend);

      // Update stats
      setStats({
        totalOrders:
          ordersResponse.data.totalCount ||
          ordersResponse.data.orders?.length ||
          0,
        activeESIMs:
          ordersResponse.data.orders?.filter((o) => o.status === "IN_USE")
            .length || 0,
        pendingESIMs:
          ordersResponse.data.orders?.filter((o) => o.status === "GOT_RESOURCE")
            .length || 0,
        recentTransactions: transactions.length,
      });

      // Update refresh timestamp
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // Calculate top destinations from orders
  const topDestinations = useMemo(() => {
    if (!recentOrders || recentOrders.length === 0) {
      return [];
    }

    // Helper to extract full location name from package data
    const getLocationName = (order) => {
      // Try to get location from various properties
      if (order.package?.location) {
        return order.package.location;
      }

      if (order.locationCode) {
        return order.locationCode;
      }

      // For package names, keep the full name instead of splitting
      if (order.package?.name) {
        // Try to identify if this is a multi-country package
        if (
          order.package.name.includes("&") ||
          order.package.name.includes(",") ||
          order.package.name.includes(" and ")
        ) {
          return "Multi-Region";
        }
        return order.package.name;
      }

      if (order.packageType) {
        return order.packageType;
      }

      if (order.packageName) {
        return order.packageName;
      }

      return "Unknown";
    };

    // Extract location data from orders
    const destinations = recentOrders.reduce((acc, order) => {
      const location = getLocationName(order);
      acc[location] = (acc[location] || 0) + 1;
      return acc;
    }, {});

    // Convert to array and sort by count
    return Object.entries(destinations)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [recentOrders]);

  useEffect(() => {
    dispatch(fetchCreditBalance());
    fetchDashboardData();
  }, [dispatch, fetchDashboardData]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Fade in={true} timeout={800}>
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Box>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight="bold"
                color="primary"
              >
                {t("dashboard.welcome", { username: user?.username })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("dashboard.lastUpdated")}: {lastRefresh.toLocaleTimeString()}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchDashboardData}
              disabled={loadingStats}
              sx={{ borderRadius: 2 }}
            >
              {t("common.actions.refresh")}
            </Button>
          </Box>

          {loadingStats && <LinearProgress sx={{ mb: 2 }} />}

          <DashboardStats stats={stats} loading={loadingStats} />

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              {/* Credit Balance Card */}
              <Box sx={{ mb: 3 }}>
                <CreditBalanceCard balance={balance} loading={loading} />
              </Box>

              {/* Top Destinations Card */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("dashboard.topPurchases")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {topDestinations.length > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        minHeight: "200px", // Ensure minimum height
                      }}
                    >
                      {topDestinations.map((destination, index) => (
                        <Box
                          key={destination.name}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography
                              variant="body2"
                              fontWeight={index === 0 ? "bold" : "normal"}
                            >
                              {destination.name}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={destination.count}
                            color={index === 0 ? "primary" : "default"}
                            variant={index === 0 ? "filled" : "outlined"}
                          />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography
                      color="text.secondary"
                      align="center"
                      sx={{ mt: 3 }}
                    >
                      {t("dashboard.noDestinationData")}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Box>
                      <Typography variant="h6">
                        {t("dashboard.creditUsageTrend")}
                      </Typography>
                      <Box display="flex" alignItems="center" mt={0.5}>
                        {dataUsageTrend.increasing ? (
                          <TrendingUpIcon
                            color="success"
                            fontSize="small"
                            sx={{ mr: 0.5 }}
                          />
                        ) : (
                          <TrendingDownIcon
                            color="error"
                            fontSize="small"
                            sx={{ mr: 0.5 }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          color={
                            dataUsageTrend.increasing
                              ? "success.main"
                              : "error.main"
                          }
                        >
                          {dataUsageTrend.percentage}%{" "}
                          {dataUsageTrend.increasing
                            ? t("dashboard.increase")
                            : t("dashboard.decrease")}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="text"
                      onClick={() => navigate("/transactions")}
                    >
                      {t("common.actions.viewAll")}
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <SpendingTrendsChart
                    transactionStats={transactionStats}
                    loading={loadingStats}
                  />
                </CardContent>
              </Card>

              {/* ESIM Status Distribution */}
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="h6">
                      {t("dashboard.recentTransactions")}
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => navigate("/transactions")}
                    >
                      {t("common.actions.viewAll")}
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <RecentTransactionsTable
                    transactions={transactionStats.slice(0, 5)}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="h6">
                      {t("dashboard.recentActivity")}
                    </Typography>
                    <Button variant="text" onClick={() => navigate("/orders")}>
                      {t("common.actions.viewAll")}
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <ESIMActivityTimeline
                    activities={recentOrders}
                    loading={loadingStats}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6} sx={{ mt: 2 }}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="h6">
                    {t("dashboard.esimStatusDistribution")}
                  </Typography>
                  <Button variant="text" onClick={() => navigate("/orders")}>
                    {t("common.actions.viewAll")}
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <ESIMStatusChart orders={recentOrders} loading={loadingStats} />
              </CardContent>
            </Card>
          </Grid>
        </Box>
      </Fade>
    </Container>
  );
};

export default Dashboard;
